import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  UserIcon,
  CreditCardIcon,
  CogIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { PageHeader, ListSkeleton } from '../../lib/commonUtils';
import { useLanguage } from '../../context/LanguageContext';
import useAuthStore from '../../store/authStore';

const DeleteAccountModal = ({ onClose }) => {
  const { t } = useLanguage();
  const { logout } = useAuthStore();
  const [confirmed, setConfirmed] = useState(false);

  const handleDelete = () => {
    onClose();
    toast.info('To permanently delete your account, please contact us at hello@valoria.ai', { autoClose: 8000 });
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
      <div className="controlled-glass rounded-[32px] p-10 max-w-md w-full shadow-2xl inner-highlight relative">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 text-white/20 hover:text-white transition-colors">
          <XMarkIcon className="h-5 w-5" />
        </button>
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-white tracking-tight mb-2">{t('deleteAccount')}</h3>
          <p className="text-sm text-white/40 mb-8 font-medium">{t('deleteAccountDesc')}</p>

          <label className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl mb-8 cursor-pointer group text-left">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="w-4 h-4 rounded accent-red-500 cursor-pointer"
            />
            <span className="text-xs font-bold text-white/50 group-hover:text-white/70 transition-colors">
              I understand this action cannot be undone
            </span>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={onClose}
              className="btn py-4 rounded-xl bg-white/5 border border-white/10 text-white font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={!confirmed}
              className={`btn py-4 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${confirmed ? 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-600/20' : 'bg-white/5 text-white/20 cursor-not-allowed border border-white/5'}`}
            >
              {t('deleteAccount')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Settings = () => {
  const { t } = useLanguage();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const settingsSections = [
    {
      id: 'account',
      title: t('accountSettings'),
      icon: UserIcon,
      description: t('accountSettingsDesc'),
      items: [
        { label: t('profileInfo'), action: t('goToProfile'), link: '/profile' },
        { label: t('passwordSecurity'), action: t('manage'), link: '/profile?tab=security' },
      ],
    },
    {
      id: 'billing',
      title: t('billingSub'),
      icon: CreditCardIcon,
      description: t('billingSubDesc'),
      items: [
        {
          label: t('currentPlanLabel'),
          actions: [
            { label: t('viewDetails'), link: '/billing?tab=current' },
            { label: t('changePlan'), link: '/billing?tab=plans' },
          ],
        },
        { label: t('billingHistoryTitle'), action: t('view'), link: '/billing?tab=history' },
      ],
    },
    {
      id: 'advanced',
      title: t('advanced'),
      icon: CogIcon,
      description: t('advancedDesc'),
    },
  ];

  const AdvancedSection = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-white tracking-tight">{t('advancedSettings')}</h3>

      <div className="space-y-4">
        <div className="p-6 sm:p-4 bg-white/5 border border-white/10 rounded-[24px] sm:rounded-xl group hover:bg-white/10 transition-all inner-highlight">
          <h4 className="text-base font-bold text-white tracking-tight mb-2 uppercase tracking-widest text-[11px] sm:text-[10px]">{t('dataExport')}</h4>
          <p className="text-sm sm:text-xs font-medium text-white/30 mt-1 font-medium italic mb-6 sm:mb-4">
            {t('dataExportDesc')}
          </p>
          <button
            onClick={() => toast.info('Your data export request has been received. We will send a download link to your registered email within 24 hours. Contact hello@valoria.ai for assistance.', { autoClose: 8000 })}
            className="btn btn-secondary w-full sm:w-auto px-8 py-3 sm:py-2 rounded-xl sm:rounded-lg border border-white/10 bg-white/5 backdrop-blur-md hover:bg-white/10 hover:border-white/20 transition-all text-xs sm:text-[10px] font-bold uppercase tracking-widest"
          >
            {t('requestExport')}
          </button>
        </div>

        <div className="p-6 sm:p-4 border border-red-500/20 bg-red-500/5 rounded-[24px] sm:rounded-xl inner-highlight">
          <div className="flex items-center mb-4 sm:mb-3">
            <ExclamationTriangleIcon className="h-5 w-5 sm:h-4 w-4 text-red-500 mr-3 sm:mr-2" />
            <h4 className="text-sm sm:text-xs font-bold text-red-500 tracking-tighter uppercase tracking-widest">{t('dangerZone')}</h4>
          </div>
          <p className="text-sm sm:text-xs font-medium text-white/30 mt-1 font-medium italic mb-8 sm:mb-5">
            {t('deleteAccountDesc')}
          </p>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="btn bg-red-600 border border-red-500 text-white rounded-xl sm:rounded-lg py-3 sm:py-2 px-8 text-xs sm:text-[10px] font-bold uppercase tracking-widest hover:bg-red-500 transition-all shadow-lg shadow-red-600/20 w-full sm:w-auto"
          >
            {t('deleteAccount')}
          </button>
        </div>
      </div>
    </div>
  );

  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader title={t('settings')} subtitle={t('settingsDesc')} />

      {loading ? (
        <ListSkeleton rows={3} />
      ) : !activeSection ? (
        <div className="grid gap-4">
          {settingsSections.map((section) => (
            <div
              key={section.id}
              className="controlled-glass rounded-[32px] sm:rounded-2xl p-6 sm:p-4 inner-highlight group hover:border-white/20 transition-all duration-300"
            >
              <div className="flex items-start sm:items-center mb-8 sm:mb-4">
                <div className="w-12 h-12 sm:w-10 sm:h-10 bg-white/5 border border-white/10 rounded-2xl sm:rounded-xl flex items-center justify-center mr-5 sm:mr-3.5 group-hover:bg-primary-500/10 group-hover:border-primary-500/20 transition-all shadow-lg">
                  <section.icon className="h-6 w-6 sm:h-5 w-5 text-white/40 group-hover:text-primary-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl sm:text-[15px] font-bold text-white tracking-tight">{section.title}</h3>
                  <p className="text-sm sm:text-xs font-medium text-white/30 mt-1 font-medium italic">{section.description}</p>
                </div>
                {section.id === 'advanced' && (
                  <button
                    onClick={() => setActiveSection(section.id)}
                    className="p-3 rounded-xl bg-white/5 border border-white/10 text-white/20 hover:text-white transition-all ml-4"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                )}
              </div>

              {section.items && (
                <div className="space-y-3">
                  {section.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col sm:flex-row sm:items-center justify-between py-5 sm:py-3.5 border-b border-white/5 last:border-none group/item"
                    >
                      <span className="text-sm sm:text-xs font-bold text-white/60 group-hover/item:text-white transition-colors tracking-wide">{item.label}</span>
                      <div className="flex space-x-6 sm:space-x-4 self-end sm:self-auto pt-3 sm:pt-0">
                        {item.actions ? (
                          item.actions.map((action, i) => (
                            <Link
                              key={i}
                              to={action.link}
                              className="text-[10px] font-bold text-primary-400 uppercase tracking-[0.15em] hover:text-primary-300 transition-all"
                            >
                              {action.label}
                            </Link>
                          ))
                        ) : (
                          <Link
                            to={item.link}
                            className="text-[10px] font-bold text-primary-400 uppercase tracking-[0.15em] hover:text-primary-300 transition-all"
                          >
                            {item.action}
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="controlled-glass rounded-[32px] sm:rounded-2xl p-8 sm:p-5 inner-highlight shadow-2xl">
          <div className="flex items-center mb-10 sm:mb-6">
            <button
              onClick={() => setActiveSection(null)}
              className="p-3 sm:p-2.5 rounded-xl sm:rounded-lg bg-white/5 border border-white/10 text-white/40 hover:text-white transition-all mr-6 sm:mr-4"
            >
              <ArrowLeftIcon className="h-5 w-5 sm:h-4 w-4" />
            </button>
            <div>
              <h2 className="text-3xl sm:text-2xl font-bold text-white tracking-tight">
                {settingsSections.find((s) => s.id === activeSection)?.title}
              </h2>
              <p className="text-[10px] text-white/20 font-bold uppercase tracking-[0.2em] mt-1 italic">
                {t('advancedConfig')}
              </p>
            </div>
          </div>

          {activeSection === 'advanced' && <AdvancedSection />}
        </div>
      )}

      {showDeleteModal && <DeleteAccountModal onClose={() => setShowDeleteModal(false)} />}
    </div>
  );
};

export default Settings;